import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';

const DEFAULTS = {
  siteName: 'Wilvada Peptide Solutions',
  description:
    'Wilvada LC supplies research-grade peptides and laboratory equipment. HPLC-verified purity, every order ships with a Certificate of Analysis. Strictly for in-vitro and pre-clinical research — not for human or veterinary use.',
  image:
    'https://customer-assets.emergentagent.com/job_peptide-research-hub-6/artifacts/kowhmc7f_Gemini_Generated_Image_tlwfymtlwfymtlwf__1_-removebg.png',
};

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (data) => {
  let el = document.getElementById('jsonld-schema');
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = 'jsonld-schema';
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

/**
 * Drop-in SEO meta updater. Use on any non-admin page.
 * Pass `title`, `description`, `canonical`, optional `image`, optional `jsonLd` (object).
 * Side-effects only — renders nothing.
 */
export default function SEO({ title, description, canonical, image, jsonLd, noIndex }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdmin) {
      setJsonLd(null);
      setMeta('robots', 'noindex,nofollow');
      return;
    }
    const fullTitle = title ? `${title} | ${DEFAULTS.siteName}` : DEFAULTS.siteName;
    document.title = fullTitle;
    const desc = description || DEFAULTS.description;
    const img = image || DEFAULTS.image;
    const url =
      canonical ||
      (typeof window !== 'undefined' ? window.location.origin + location.pathname : '');

    setMeta('description', desc);
    setMeta('robots', noIndex ? 'noindex,nofollow' : 'index,follow');
    setLink('canonical', url);

    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:image', img, 'property');
    setMeta('og:url', url, 'property');
    setMeta('og:site_name', DEFAULTS.siteName, 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', desc);
    setMeta('twitter:image', img);

    setJsonLd(jsonLd || null);

    trackPageView(location.pathname + location.search);
  }, [isAdmin, title, description, canonical, image, jsonLd, noIndex, location.pathname, location.search]);

  return null;
}
